import React from "react";
import { Box, Button, Text } from "@showitinc/showit-wc";

import AppConstants from "../constants/AppConstants";
import DropShadowProperties from "../app2components/panel/element/DropShadowProperties";
import ElementUtils from "../utils/ElementUtils";
import CornerStyleProperties from "./CornerStyleProperties";
import { gatherProps } from "../utils/Merge/MergeUtils.mjs";
import {
  BORDER_RADIUS,
  OPACITY,
  BLUR,
  SHADOW,
  SHADOW_STYLE,
} from "../constants/MergeConstants";
import { elementSyncUtils } from "../utils/SyncUtils";
import SplitMergeWrapper from "../app2components/base/SplitMergeWrapper";
import { initFormatter } from "../utils/PropertyFormatters";
import { prepareInputUtils } from "../utils/InputUtils";
import { GROUP } from "@/constants/DataTypes";
import UIActions from "../actions/UIActions";
import { DROP_SHADOW_ELEMENTS, INSET_SHADOW_ELEMENTS } from "../utils/Element/EffectsPropertyMaps";
import {
  BlurInput,
  OpacityInput,
} from "../utils/Element/EffectsInputs";
import { useAccountState } from "../hooks/query/useAccount";

const {
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_HTML_TEXT,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_SVG,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_GALLERY,
  ELEMENT_TYPE_IFRAME,
  ELEMENT_TYPE_LOTTIE,
  PANEL_SECTION_ELEMENT_SIMPLE,
  PANEL_SECTION_ELEMENT_LINE,
  PANEL_SECTION_ELEMENT_TEXT,
  PANEL_SECTION_ELEMENT_ICON,
  PANEL_SECTION_ELEMENT_GRAPHIC,
  PANEL_SECTION_ELEMENT_SVG,
  PANEL_SECTION_ELEMENT_VIDEO,
  PANEL_SECTION_ELEMENT_GALLERY,
  PANEL_SECTION_ELEMENT_LOTTIE,
  CORNER_STYLE_OPTION_NONE,
} = AppConstants;

const BORDER_RADIUS_BLACKLIST = [ELEMENT_TYPE_LINE, GROUP];

const MAPPED_ELEMENT_TYPES = [
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_HTML_TEXT,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_SVG,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_GALLERY,
  ELEMENT_TYPE_IFRAME,
  ELEMENT_TYPE_LOTTIE,
];

const ELEMENT_TYPE_TO_STYLE_PANEL = {
  [ELEMENT_TYPE_SIMPLE]: {
    name: "Shape Style",
    section: PANEL_SECTION_ELEMENT_SIMPLE,
  },
  [ELEMENT_TYPE_LINE]: {
    name: "Line Style",
    section: PANEL_SECTION_ELEMENT_LINE,
  },
  [ELEMENT_TYPE_TEXT]: {
    name: "Text Style",
    section: PANEL_SECTION_ELEMENT_TEXT,
  },
  [ELEMENT_TYPE_HTML_TEXT]: {
    name: "Text Style",
    section: PANEL_SECTION_ELEMENT_TEXT,
  },
  [ELEMENT_TYPE_ICON]: {
    name: "Icon Settings",
    section: PANEL_SECTION_ELEMENT_ICON,
  },
  [ELEMENT_TYPE_GRAPHIC]: {
    name: "Image",
    section: PANEL_SECTION_ELEMENT_GRAPHIC,
  },
  [ELEMENT_TYPE_SVG]: { name: "SVG", section: PANEL_SECTION_ELEMENT_SVG },
  [ELEMENT_TYPE_VIDEO]: { name: "Video", section: PANEL_SECTION_ELEMENT_VIDEO },
  [ELEMENT_TYPE_GALLERY]: { name: "Gallery Settings", section: PANEL_SECTION_ELEMENT_GALLERY },
  [ELEMENT_TYPE_LOTTIE]: { name: "Lottie", section: PANEL_SECTION_ELEMENT_LOTTIE },
  [AppConstants.ELEMENT_TYPE_IFRAME]: {
    name: "Embed Info",
    section: AppConstants.PANEL_SECTION_ELEMENT_IFRAME,
  },
};

const ElementPropertiesEffects = ({ element, isLocked, layout, onPropUpdate, preparedElement }) => {
  const user = useAccountState();
  const isAdmin = user.get("isAdmin", false);
  const _onPropChange = (propInfo) => onPropUpdate?.(propInfo);

  const _elementHasDropShadow = (elementType) => DROP_SHADOW_ELEMENTS.includes(elementType);

  const _elementHasInsetShadow = (elementType) => INSET_SHADOW_ELEMENTS.includes(elementType);

  const _elementHasBorderRadius = (elementType) => !BORDER_RADIUS_BLACKLIST.includes(elementType);

  const elementType = element.get("type", true);
  const content = element.get("content", false);

  const usesMappedSystem = MAPPED_ELEMENT_TYPES.includes(elementType);
  const stylePanelInfo = ELEMENT_TYPE_TO_STYLE_PANEL[elementType];

  // If this element uses the mapper system, show redirect message
  if (usesMappedSystem && stylePanelInfo) {
    const handleOpenStylePanel = () => {
      UIActions.toggleElementPropertiesSection({
        name: stylePanelInfo.section,
      });
    };

    return (
      <Box px="4" py="3">
        <Text variant="body">
          Effects have moved to the <strong>{stylePanelInfo.name}</strong> tab, under the
          "Customize" dropdown menu.
        </Text>
        <Box mt="2">
          <Button
            fullWidth
            variant="outline"
            onSIClick={handleOpenStylePanel}
            data-testid="open-style-panel-button"
          >
            Open {stylePanelInfo.name}
          </Button>
        </Box>
      </Box>
    );
  }

  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    element,
    _onPropChange,
    layout,
  );

  const { onChange } = initFormatter(onPropUpdate, getPropName);
  const supportsDropShadow =
    _elementHasDropShadow(elementType) || ElementUtils.isSimpleGallery(elementType, content);

  const elemProps = gatherProps(
    preparedElement,
    layout,
    ["w", "h", OPACITY, BLUR, BORDER_RADIUS, SHADOW_STYLE],
    true,
  );

  const { canMerge, prepareValue, isSyncMixed } = prepareInputUtils(
    layout,
    preparedElement,
    elemProps,
  );

  const opacity = prepareValue(OPACITY, 100);
  const blur = prepareValue(BLUR, 0);
  const borderRadius = prepareValue(BORDER_RADIUS, CORNER_STYLE_OPTION_NONE);
  const height = elemProps.get("h");
  const width = elemProps.get("w");
  const shadowProps = prepareValue(SHADOW);

  return (
    <>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(OPACITY)}
        sync={isSynced(OPACITY)}
        toggleSync={() => toggleSyncedProp(OPACITY)}
      >
        <OpacityInput
          context={canMerge(OPACITY)}
          disabled={isLocked}
          onBeforeChange={onChange(OPACITY, opacity)}
          onInput={onChange(OPACITY, opacity, false)}
          value={opacity}
        />
      </SplitMergeWrapper>

      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(BLUR)}
        sync={isSynced(BLUR)}
        toggleSync={() => toggleSyncedProp(BLUR)}
      >
        <BlurInput
          context={canMerge(BLUR)}
          disabled={isLocked}
          onBeforeChange={onChange(BLUR, blur)}
          onInput={onChange(BLUR, blur, false)}
          value={blur}
        />
      </SplitMergeWrapper>

      {supportsDropShadow && (
        <DropShadowProperties
          disableInsetShadow={!_elementHasInsetShadow(elementType)}
          getPropName={getPropName}
          isLocked={isLocked}
          layout={layout}
          mixedSync={isSyncMixed(SHADOW_STYLE)}
          onPropUpdate={_onPropChange}
          shadowProps={shadowProps}
          sync={isSynced(SHADOW_STYLE)}
          toggleSync={() => toggleSyncedProp(SHADOW_STYLE)}
        />
      )}
      {_elementHasBorderRadius(elementType) && (
        <CornerStyleProperties
          isLocked={isLocked}
          layout={layout}
          length={height < width ? height : width}
          mixedSync={isSyncMixed(BORDER_RADIUS)}
          onPropUpdate={_onPropChange}
          propName={getPropName(BORDER_RADIUS)}
          sync={isSynced(BORDER_RADIUS)}
          toggleSync={() => toggleSyncedProp(BORDER_RADIUS)}
          usePresets
          value={borderRadius}
        />
      )}
    </>
  );
};

export default ElementPropertiesEffects;
